/**
 * Lumistelle Carousel Features Module
 * Version 6.0 - With Gallery Modal Integration
 * Handles all user actions and reactive updates for product cards.
 */
console.log('[LumistelleCarousel] Features file loaded');

(function(window, document) {
  'use strict';

  const LumistelleCarouselFeatures = {
    // ======================================================================
    // GALLERY MODAL LOADER STATE
    // ======================================================================
    _galleryModalLoading: false,
    _galleryModalReady: false,
    _galleryModalQueue: null, // Stores pending open() args if script is still loading
    _galleryHoverSetup: false,

    // ======================================================================
    // CORE LOGIC CASCADE (REACTIVE UPDATES)
    // ======================================================================
    handleVariantChange(productEl, config) {
      if (!config) {
        config = this.getConfigFromSection(productEl);
        if (!config) return;
      }

      window.Lumistelle.engine.batchDOMOperations({
        write: () => {
          this.updateAvailableOptions(productEl);

          const activeSize = productEl.querySelector('.lumistelle-carousel__size.active');
          if (activeSize?.classList.contains('out-of-stock')) {
            activeSize.classList.remove('active');
            const firstAvailableSize = productEl.querySelector('.lumistelle-carousel__size:not(.out-of-stock)');
            if (firstAvailableSize) {
              firstAvailableSize.classList.add('active');
              this.updateAvailableOptions(productEl);
            }
          }

          const selectedVariant = this.getSelectedVariant(productEl);
          const productData = window.Lumistelle.engine.variantData.get(parseInt(productEl.dataset.productId, 10));

          // Update image to match selected variant
          if (selectedVariant && productData && productData.images) {
            
            // --- START: THE DEFINITIVE FIX ---
            // This robustly checks for the image ID from BOTH possible data structures.
            let imageId = null;
            if (selectedVariant.featured_image_id) {
              // Path 1: For Liquid-rendered data
              imageId = selectedVariant.featured_image_id;
            } else if (selectedVariant.featured_image && selectedVariant.featured_image.id) {
              // Path 2: For API-rendered data (from homepage "Load More")
              imageId = selectedVariant.featured_image.id;
            }
            // --- END: THE DEFINITIVE FIX ---

            if (imageId) {
              const newIndex = productData.images.findIndex(img => img.id === imageId);
              if (newIndex !== -1 && newIndex !== productEl.currentImageIndex) {
                window.Lumistelle.engine.slideToImage(productEl, newIndex);
              }
            }
          }

          const variantForDisplay = selectedVariant || productData.variants.find(v => v.available) || productData.variants[0];
          
          this.updatePriceAndStock(productEl, variantForDisplay, config);
          this.updateQuickAddButton(productEl, selectedVariant, config);
          this.updateWishlistButtonVariant(productEl, selectedVariant);
        }
      });
    },

    updateAvailableOptions(productEl) {
      const productData = window.Lumistelle.engine.variantData.get(parseInt(productEl.dataset.productId, 10));
      if (!productData) return;

      const selectedColor = productEl.querySelector('.lumistelle-carousel__color.active')?.dataset.color;
      const selectedSize = productEl.querySelector('.lumistelle-carousel__size.active')?.dataset.size;

      productEl.querySelectorAll('.lumistelle-carousel__size').forEach(btn => {
        const isAvailable = productData.variants.some(v => (!selectedColor || v.option1 === selectedColor) && v.option2 === btn.dataset.size && v.available);
        btn.classList.toggle('out-of-stock', !isAvailable);
        btn.disabled = !isAvailable;
      });

      productEl.querySelectorAll('.lumistelle-carousel__color').forEach(btn => {
        const isAvailable = productData.variants.some(v => v.option1 === btn.dataset.color && (!selectedSize || v.option2 === selectedSize) && v.available);
        btn.classList.toggle('out-of-stock', !isAvailable);
        btn.disabled = !isAvailable;
      });
    },

    updatePriceAndStock(productEl, targetVariant, config) {
      if (!targetVariant || !config) return;

      const displayData = {
        price: targetVariant.price,
        compare_at_price: targetVariant.compare_at_price,
        available: targetVariant.available,
        inventory_quantity: targetVariant.inventory_quantity,
        discount_percentage: targetVariant.compare_at_price > targetVariant.price ? Math.round((targetVariant.compare_at_price - targetVariant.price) * 100 / targetVariant.compare_at_price) : 0
      };

      // === PHASE 4: Get cached elements once and pass to both methods ===
      const cached = window.Lumistelle.engine._getCardElements(productEl);
      
      window.Lumistelle.engine.updateProductPrice(productEl, displayData, config, cached);
      window.Lumistelle.engine.updateStockIndicator(productEl, displayData, cached);
    },

    updateQuickAddButton(productEl, selectedVariant, config) {
      // === PHASE 4: Use cached elements ===
      const cached = window.Lumistelle.engine._getCardElements(productEl);
      
      const button = cached.quickAddButton;
      if (!button) return;

      if (config.showQuickAdd === false) {
        button.style.display = 'none';
        return;
      }
      button.style.display = '';

      const textEl = cached.quickAddText;
      if (textEl) {
        if (selectedVariant && selectedVariant.available) {
          button.disabled = false;
          textEl.textContent = 'Add to Cart';
        } else {
          button.disabled = true;
          textEl.textContent = 'Sold Out';
        }
      }
    },

    updateWishlistButtonVariant(productEl, selectedVariant) {
      // === PHASE 4: Use cached elements ===
      const cached = window.Lumistelle.engine._getCardElements(productEl);
      
      const wishlistButton = cached.wishlistButton;
      if (!wishlistButton) return;

      if (selectedVariant) {
        wishlistButton.setAttribute('data-variant', selectedVariant.id);
      }
    },

    // ======================================================================
    // USER ACTION HANDLERS
    // ======================================================================
    handleClick(e) {
      const productEl = e.target.closest('.lumistelle-carousel__product');
      if (!productEl) return;

      // --- Gallery Modal Trigger ---
      const galleryTrigger = e.target.closest('.lum-gallery-trigger');
      if (galleryTrigger) {
        e.preventDefault();
        e.stopPropagation();
        const productId = parseInt(productEl.dataset.productId, 10);
        const startIndex = productEl.currentImageIndex || 0;
        // === LITE CARD: no data island — fetch + register on first open ===
        const engine = window.Lumistelle?.engine;
        if (engine && !engine.variantData.has(productId) && productEl.dataset.handle) {
          galleryTrigger.disabled = true;
          this._ensureProductData(productId, productEl.dataset.handle)
            .then(() => this._openGalleryModal(productId, startIndex, galleryTrigger))
            .catch(err => console.warn('[LumistelleGallery] data fetch failed:', err))
            .finally(() => { galleryTrigger.disabled = false; });
          return;
        }
        this._openGalleryModal(productId, startIndex, galleryTrigger);
        return;
      }

      // --- Variant Click Logic ---
      const colorSwatch = e.target.closest('.lumistelle-carousel__color');
      const sizeSelector = e.target.closest('.lumistelle-carousel__size');

      if (colorSwatch || sizeSelector) {
        e.preventDefault();
        const clickedOption = colorSwatch || sizeSelector;
        if (clickedOption.disabled) return;

        clickedOption.parentElement.querySelectorAll('.active').forEach(el => el.classList.remove('active'));
        clickedOption.classList.add('active');
        
        // Use the robust helper to get the config
        const config = this.getConfigFromSection(productEl);
        if (!config) return;
        this.handleVariantChange(productEl, config);
        return;
      }
      
      // --- Action Button Click Logic ---
      const quickAddBtn = e.target.closest('.lumistelle-carousel__quick-add');
      if (quickAddBtn) { e.preventDefault(); this.handleQuickAdd(quickAddBtn); return; }

    },


    /**
     * LITE CARD: fetch /products/{handle}.js and register the product into
     * engine.variantData in the SAME shape the PDP gallery controller
     * registers (incl. swatch_url per image + colorToSwatchUrlMap, which
     * the modal's thumbnails and color swatches render from).
     * One fetch per product per session (variantData is the cache).
     */
    async _ensureProductData(productId, handle) {
      const engine = window.Lumistelle?.engine;
      if (!engine) throw new Error('engine unavailable');
      if (engine.variantData.has(productId)) return;

      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) throw new Error(`HTTP ${response.status} for ${handle}`);
      const p = await response.json();

      const sep = (src) => (src && src.includes('?') ? '&' : '?');
      const buildSrcset = (src) => {
        if (!src) return '';
        const s = sep(src);
        return [300, 600, 900, 1200, 1800, 2400]
          .map(w => `${src}${s}width=${w} ${w}w`).join(', ');
      };
      // Mirrors the engine/PDP createSwatchUrl (100x125 top-crop thumb)
      const createSwatchUrl = (src) => {
        if (!src) return '';
        try {
          const [base, params] = src.split('?');
          const parts = base.split('.');
          const ext = parts.pop();
          return `${parts.join('.')}_100x125_crop_top.${ext}${params ? '?' + params : ''}`;
        } catch (e) { return src; }
      };

      const optionsWithValues = (p.options || []).map((opt, idx) => {
        const name = typeof opt === 'string' ? opt : opt.name;
        const values = [...new Set(p.variants.map(v =>
          v.options ? v.options[idx] : v[`option${idx + 1}`]
        ).filter(Boolean))];
        return { name, values };
      });

      const mediaImages = (p.media || []).filter(m => m.media_type === 'image');
      const imageIdToSrcMap = {};
      mediaImages.forEach(m => { imageIdToSrcMap[m.id] = m.src; });

      const images = mediaImages.length
        ? mediaImages.map(m => ({
            id: m.id,
            src: `${m.src}${sep(m.src)}width=1200`,
            swatch_url: createSwatchUrl(m.src),
            srcset: buildSrcset(m.src)
          }))
        : (p.images || []).map(src => ({
            id: null,
            src: `${src}${sep(src)}width=1200`,
            swatch_url: createSwatchUrl(src),
            srcset: buildSrcset(src)
          }));

      const colorIdx = optionsWithValues.findIndex(o => o.name.toLowerCase() === 'color');
      const mediaToColorMap = {};
      const colorToSwatchUrlMap = {};
      if (colorIdx !== -1) {
        const colorsSeen = new Set();
        p.variants.forEach(v => {
          const color = v.options ? v.options[colorIdx] : v[`option${colorIdx + 1}`];
          const mid = v.featured_media?.id || v.featured_image?.id;
          if (mid && color && !(mid in mediaToColorMap)) mediaToColorMap[mid] = color;
          if (color && mid && !colorsSeen.has(color) && imageIdToSrcMap[mid]) {
            colorToSwatchUrlMap[color] = createSwatchUrl(imageIdToSrcMap[mid]);
            colorsSeen.add(color);
          }
        });
      }

      engine.variantData.set(productId, {
        id: productId,
        handle: handle,
        url: p.url || `/products/${handle}`,
        title: p.title || '',
        price: p.price,
        compare_at_price: p.compare_at_price,
        images: images,
        variants: p.variants.map(v => ({
          id: v.id,
          title: v.title,
          option1: v.option1 ?? (v.options ? v.options[0] : null),
          option2: v.option2 ?? (v.options ? v.options[1] : null),
          option3: v.option3 ?? (v.options ? v.options[2] : null),
          price: v.price,
          compare_at_price: v.compare_at_price,
          available: v.available,
          featured_image_id: v.featured_media?.id || v.featured_image?.id || null
        })),
        options_with_values: optionsWithValues,
        mediaToColorMap: mediaToColorMap,
        colorToSwatchUrlMap: colorToSwatchUrlMap
      });
    },

    // ======================================================================
    // GALLERY MODAL: DYNAMIC LOADER & OPENER
    // ======================================================================

    /**
     * Open the gallery modal, loading the script on-demand if needed.
     * @param {number} productId
     * @param {number} startIndex
     * @param {HTMLElement} triggerEl
     */
    _openGalleryModal(productId, startIndex, triggerEl) {
      // Already loaded — open immediately
      if (this._galleryModalReady && window.LumistelleGalleryModal) {
        window.LumistelleGalleryModal.open(productId, startIndex, triggerEl);
        return;
      }

      // Script is loading — queue this open request
      if (this._galleryModalLoading) {
        this._galleryModalQueue = { productId, startIndex, triggerEl };
        return;
      }

      // First load — dynamically inject the script
      this._galleryModalLoading = true;
      this._galleryModalQueue = { productId, startIndex, triggerEl };

      const script = document.createElement('script');
      script.src = window.Shopify?.cdnHost
        ? `https://${window.Shopify.cdnHost}/s/files/1/lumistelle-gallery-modal.js`
        : '/assets/lumistelle-gallery-modal.js'; // Fallback for standard Shopify asset path
      
      // Use the Shopify asset_url pattern — the actual URL will be set in theme.liquid
      // or via the asset pipeline. This covers both CDN and local dev.
      if (window.LumistelleAssets?.galleryModal) {
        script.src = window.LumistelleAssets.galleryModal;
      }

      script.onload = () => {
        this._galleryModalLoading = false;
        this._galleryModalReady = true;

        // Process queued open request
        if (this._galleryModalQueue && window.LumistelleGalleryModal) {
          const q = this._galleryModalQueue;
          this._galleryModalQueue = null;
          window.LumistelleGalleryModal.open(q.productId, q.startIndex, q.triggerEl);
        }
      };

      script.onerror = () => {
        this._galleryModalLoading = false;
        this._galleryModalQueue = null;
        console.error('[LumistelleGalleryModal] Failed to load gallery modal script');
      };

      document.head.appendChild(script);
    },

    /**
     * Setup desktop hover preloading for gallery triggers.
     * Called once after first instance init.
     */
    setupGalleryHoverPreload() {
      if (this._galleryHoverSetup) return;
      this._galleryHoverSetup = true;

      // Only on devices with hover capability
      if (!window.matchMedia('(hover: hover)').matches) return;

      document.addEventListener('mouseover', (e) => {
        const trigger = e.target.closest('.lum-gallery-trigger');
        if (!trigger) return;

        const productEl = trigger.closest('.lumistelle-carousel__product');
        if (!productEl) return;

        const productId = parseInt(productEl.dataset.productId, 10);
        if (!productId) return;

        // Preload the modal script if not yet loaded
        if (!this._galleryModalReady && !this._galleryModalLoading) {
          const script = document.createElement('script');
          if (window.LumistelleAssets?.galleryModal) {
            script.src = window.LumistelleAssets.galleryModal;
          } else {
            script.src = '/assets/lumistelle-gallery-modal.js';
          }
          script.onload = () => {
            this._galleryModalLoading = false;
            this._galleryModalReady = true;
            // Preload images for the hovered product
            if (window.LumistelleGalleryModal) {
              window.LumistelleGalleryModal.preloadForProduct(productId);
            }
          };
          script.onerror = () => { this._galleryModalLoading = false; };
          this._galleryModalLoading = true;
          document.head.appendChild(script);
        } else if (this._galleryModalReady && window.LumistelleGalleryModal) {
          // Script already loaded — just preload images
          window.LumistelleGalleryModal.preloadForProduct(productId);
        }
      });
    },

    // ======================================================================
    // QUICK ADD
    // ======================================================================
    async handleQuickAdd(button) {
      const productEl = button.closest('.lumistelle-carousel__product');
      const variant = this.getSelectedVariant(productEl);

      if (!variant || !variant.available) return;

      button.classList.add('loading');
      button.disabled = true;

      const updateHeaderCartCount = (itemCount) => {
        const cartCountElements = document.querySelectorAll('.cart-count');
        if (!cartCountElements.length) return;

        cartCountElements.forEach(el => {
          el.textContent = itemCount;
          if (itemCount > 0) {
            el.style.display = 'flex';
          } else {
            el.style.display = 'none';
          }
        });
      };

      try {
        // Step 1: Add the item to the cart.
        await window.Lumistelle.engine.ajax.addToCart(variant.id);

        // Step 2: Fetch the entire updated cart object to get the new state.
        const cartData = await (await fetch('/cart.js')).json();

        // Step 3: Open the cart drawer and pass the new cart data directly to it.
        if (window.CartDrawer && typeof window.CartDrawer.openWithCart === 'function') {
          window.CartDrawer.openWithCart(cartData);
        }

        button.classList.remove('loading');
        button.classList.add('success');
        button.querySelector('.lumistelle-carousel__quick-add-text').textContent = '✓ Added!';

      } catch (error) {
        console.error('Quick Add Error:', error);
        button.classList.remove('loading');
        button.classList.add('error');
        button.querySelector('.lumistelle-carousel__quick-add-text').textContent = 'Error';
        
      } finally {
        setTimeout(() => {
          button.classList.remove('success', 'error');
          const config = this.getConfigFromSection(productEl);
          if (!config) return;
          this.updateQuickAddButton(productEl, this.getSelectedVariant(productEl), config);
        }, 2000);
      }
    },

    // ======================================================================
    // HELPERS & UTILITIES
    // ======================================================================
    getSelectedVariant(productEl) {
      const productData = window.Lumistelle.engine.variantData.get(parseInt(productEl.dataset.productId, 10));
      if (!productData) return null;

      const color = productEl.querySelector('.lumistelle-carousel__color.active')?.dataset.color;
      const size = productEl.querySelector('.lumistelle-carousel__size.active')?.dataset.size;

      return productData.variants.find(variant => {
        const colorMatch = !color || variant.option1 === color;
        const sizeMatch = !size || variant.option2 === size;
        return colorMatch && sizeMatch;
      });
    },

    updateWishlistIcon(productEl, productId) {
      const wishlistBtn = productEl.querySelector('.lumistelle-carousel__wishlist');
      if (wishlistBtn) {
        const isInWishlist = window.Lumistelle.engine.getState('wishlist').includes(productId);
        wishlistBtn.classList.toggle('active', isInWishlist);
      }
    },

    getConfigFromSection(element) {
      const section = element.closest('[data-section-id]');
      if (!section) return null;

      // This is the fix: check the section element itself first, then fall back to a child.
      const configEl = section.hasAttribute('data-config')
        ? section
        : section.querySelector('[data-config]');

      if (!configEl || !configEl.dataset.config) return null;

      try {
        return JSON.parse(configEl.dataset.config);
      } catch (e) {
        console.error('Failed to parse section config:', e);
        return null;
      }
    }

  };

  window.LumistelleCarouselFeatures = LumistelleCarouselFeatures;

})(window, document);